import { useInboxFlags } from '@/composables/useInboxFlags'
import { useAuthStore } from '@/stores/authStore'

/**
 * Sends the two old inbox routes to the UNIFIED inbox while `new_inbox` is on.
 * ======================================================================
 * A redirect instead of rewiring every entry point: the menu, the navbar
 * pills, the deep links, the backend's landing route and anybody's bookmark
 * all go through a route, so catching them here covers every one of them at
 * once and leaves the V1 pages and their callers untouched.
 *
 * That is the rollback story too. Uncheck `new_inbox` in the Keystone Admin
 * and this middleware stops matching — the app is back on the V1 pages with no
 * deploy, no git, nothing to undo.
 *
 * (The intermediate V2 pages this used to forward to were retired 2026-09-28;
 * the unified inbox is now THE inbox. The old filename is kept so the global
 * middleware's order is unchanged.)
 *
 * FAILS OPEN, deliberately: anything this cannot answer — API down, flags
 * unreadable — leaves the user on V1 rather than blocking the route. That is
 * also why the flag fetch must NOT go through `useApolloClient()`: there is no
 * component instance here for its `inject`, it throws, and the redirect
 * silently never fires. `useInboxFlags` resolves the client from `useNuxtApp`
 * for exactly this reason.
 */

/** The two V1 routes the unified inbox replaces. */
const CAUGHT = new Set(['text-messages', 'follow-ups'])
const UNIFIED_ROUTE = 'inbox'

export default defineNuxtRouteMiddleware(async to => {
  if (import.meta.server) return
  if (!CAUGHT.has(to.name)) return

  const auth = useAuthStore()

  // Unauthenticated traffic belongs to the auth middleware, not here.
  if (!auth.isAuthenticated) return

  // NEVER redirect somebody the destination would reject. `/follow-ups` is the
  // LANDING route for every role, but the unified inbox is a lead-agency
  // surface — sending a company user there dropped them on a 403 they could
  // not navigate out of, on the one page they always land on (Sandy
  // 2026-08-10). They stay on V1, which is theirs.
  if (!auth.isLeadAgencyUser && !auth.isSysAdmin) return

  let flags = null
  try {
    flags = useInboxFlags()

    // Cached after the first call; only the two matching routes ever await it.
    await flags.load()
  }
  catch {
    return
  }

  if (flags?.newInbox?.value)
    return navigateTo({ name: UNIFIED_ROUTE, query: to.query }, { replace: true })
})
